import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
	Box,
	Paper,
	Typography,
	Stepper,
	Step,
	StepLabel,
	CircularProgress,
	Container,
	Button as MuiButton,
	Collapse,
	IconButton,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useQuery, useQueryClient } from 'react-query';
import { EmployeeSelfEntryForm } from './EmployeeSelfEntryForm';
import { EmployeeOnboardingAvailabilityForm } from './EmployeeOnboardingAvailabilityForm';
import { OnboardingDocumentViewer } from './OnboardingDocumentViewer';
import {
	getOnboardingPhaseTwoStatus,
	getOnboardingById,
	getOnboardingDocuments,
	OnboardingPhaseTwoStatus,
	OnboardingRecord,
	submitOnboardingPhaseTwo,
} from 'core/API/onboarding';
import { apiService } from 'core/services/apiService';
import { IOnboardingDocument } from 'core/interfaces/onboardingInterface';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import {
	getRequiredOnboardingDocuments,
	filterRequiredDocumentStatuses,
} from '../utils/onboardingValidation';
import {
	buildOnboardingStepLabels,
	extractUserAvailabilityFromUser,
	hasSavedAvailability,
	isSchedulingModuleLicensed,
	resolveInitialOnboardingStep,
	resolveOnboardingStepIndices,
} from '../utils/onboardingAvailability';
import style from './EmployeeOnboardingProgress.module.scss';
import { useAuth } from 'module/auth/context/AuthContext';
import { useWorkplaceUser } from 'module/documents/utils';
import { useUserData } from 'store/useUserData';
import { formatWorkplaceUnitNumber } from 'shared/utils/stringUtils';
import {
	resolveOnboardingPermissions,
	canViewOwnOnboardingProgress,
	canEditOwnOnboardingProgress,
} from '../utils/onboardingPermission';
import { SecurityRolePermission } from 'core/services/permissionService';
// import { Button } from 'components/ui/button';

function getWorkplaceDisplay(workplace: {
	workplaceUnitNumber?: string | null;
	name?: string | null;
	streetAddress?: string | null;
	city?: string | null;
	state?: string | null;
	postcode?: string | null;
	country?: string | null;
} | null): { name: string; address: string } | null {
	if (!workplace) return null;
	return {
		name: [
			workplace.workplaceUnitNumber
				? formatWorkplaceUnitNumber(workplace.workplaceUnitNumber)
				: null,
			workplace.name,
		]
			.filter(Boolean)
			.join(' '),
		address: [
			workplace.streetAddress,
			workplace.city,
			workplace.state,
			workplace.postcode,
			workplace.country,
		]
			.filter(Boolean)
			.join(', '),
	};
}

/**
 * EmployeeOnboardingProgress
 *
 * This component is displayed when a user logs in with isOnboardingInProgress = true
 * It shows a stepper UI to track onboarding progress and allows the employee to fill in their information
 *
 * Flow:
 * 1. Call GET api/onboarding-phase-two/status to get progress/status
 * 2. Use onboarding IRI from status to fetch full onboarding record via /api/onboardings/{id}
 * 3. Fetch document templates via api/onboarding_document_templates
 * 4. Basic info → (optional Availability when Scheduler licensed) → Documents → Submit
 */
export default function EmployeeOnboardingProgress(): JSX.Element {
	const { logout } = useAuth();
	const { currentUserRole } = useUserData();
	const { userOrg } = useWorkplaceUser();
	const onboardingPermissions = useMemo(
		() =>
			resolveOnboardingPermissions(
				(currentUserRole?.securityRolePermission ?? []) as SecurityRolePermission[],
			),
		[currentUserRole],
	);
	const canViewOwnOnboarding = canViewOwnOnboardingProgress(onboardingPermissions);
	const canEditOwnOnboarding = canEditOwnOnboardingProgress(onboardingPermissions);
	const theme = useTheme();
	const isTabletViewport = useMediaQuery(theme.breakpoints.between('sm', 'lg'));
	const [isLoading, setIsLoading] = useState(true);
	const [phaseTwoStatus, setPhaseTwoStatus] = useState<OnboardingPhaseTwoStatus | null>(null);
	const [onboardingRecord, setOnboardingRecord] = useState<OnboardingRecord | null>(null);
	const [_documentTemplates, setDocumentTemplates] = useState<IOnboardingDocument[]>([]);
	const [documentTemplatesList, setDocumentTemplatesList] = useState<IOnboardingDocument[]>([]);
	const [activeStep, setActiveStep] = useState(0);
	const [workplace, setWorkplace] = useState<any | null>(null);
	const [isSubmittingPhaseTwo, setIsSubmittingPhaseTwo] = useState(false);
	const [isJourneyExpanded, setIsJourneyExpanded] = useState(false);
	const [availabilityCompletedLocally, setAvailabilityCompletedLocally] = useState(false);
	const availabilityCompletedLocallyRef = useRef(availabilityCompletedLocally);
	availabilityCompletedLocallyRef.current = availabilityCompletedLocally;
	const { showToast } = useToastContext();
	const queryClient = useQueryClient();

	const schedulingLicensed = isSchedulingModuleLicensed(userOrg?.products);
	const authUserId = Number(localStorage.getItem('userId') || 0);

	const { data: meAvailabilityData } = useQuery(
		['user-availability', authUserId || 'me', 'onboarding-progress'],
		async () => {
			const endpoint =
				Number.isFinite(authUserId) && authUserId > 0
					? `/users/${authUserId}`
					: '/users/me';
			const response = await apiService.get(endpoint);
			return apiService.responseHandler(response);
		},
		{
			enabled: schedulingLicensed,
			refetchOnMount: 'always',
			staleTime: 0,
		},
	);

	const hasDocuments = documentTemplatesList.length > 0;
	const isSubmitted = !!onboardingRecord?.phase2CompletedAt;
	const requiredDocumentStatuses = filterRequiredDocumentStatuses(
		phaseTwoStatus?.progress?.documents || [],
		documentTemplatesList,
	);
	const stepIndices = useMemo(
		() =>
			resolveOnboardingStepIndices({
				hasDocuments,
				schedulingLicensed,
			}),
		[hasDocuments, schedulingLicensed],
	);
	const steps = useMemo(
		() =>
			buildOnboardingStepLabels({
				hasDocuments,
				schedulingLicensed,
			}),
		[hasDocuments, schedulingLicensed],
	);
	const isDocumentStep =
		stepIndices.documents != null && activeStep === stepIndices.documents && hasDocuments;
	const isTabletDocumentFocus = isTabletViewport && isDocumentStep;
	const workplaceDisplay = useMemo(() => getWorkplaceDisplay(workplace), [workplace]);
	const onboardingWorkplaceId = useMemo(() => {
		if (!onboardingRecord?.workplace) return null;
		const match = onboardingRecord.workplace.match(/\/(\d+)$/);
		return match ? parseInt(match[1], 10) : null;
	}, [onboardingRecord?.workplace]);

	const isPhase1Completed =
		phaseTwoStatus?.progress?.basic_info?.status === 'completed' ||
		phaseTwoStatus?.progress?.basic_info?.status === 'approved';
	const isAvailabilityCompleted =
		!schedulingLicensed ||
		availabilityCompletedLocally ||
		hasSavedAvailability(extractUserAvailabilityFromUser(meAvailabilityData));
	const allDocumentsCompleted = hasDocuments
		? requiredDocumentStatuses.every(
				(doc) => doc.status === 'completed' || doc.status === 'approved',
		  )
		: true;
	const canSubmitPhaseTwo =
		canEditOwnOnboarding &&
		isPhase1Completed &&
		isAvailabilityCompleted &&
		allDocumentsCompleted &&
		!isSubmitted;
	const isFormEditable = canEditOwnOnboarding && !isSubmitted;

	const canGoBack = activeStep > 0 && !isSubmitted;
	const canGoNext =
		!isSubmitted &&
		(() => {
			if (activeStep === stepIndices.basicInfo) {
				return isPhase1Completed;
			}
			if (stepIndices.availability != null && activeStep === stepIndices.availability) {
				return isAvailabilityCompleted;
			}
			if (stepIndices.documents != null && activeStep === stepIndices.documents) {
				return allDocumentsCompleted;
			}
			return false;
		})();

	/**
	 * Extract the onboarding ID from the IRI string (e.g., "/api/onboardings/1" -> 1)
	 */
	const extractIdFromIRI = (iri: string): number | null => {
		const match = iri.match(/\/(\d+)$/);
		return match ? parseInt(match[1], 10) : null;
	};

	const fetchOnboardingData = useCallback(
		async (options?: {
			proceedFromBasicInfo?: boolean;
			/** Keep the current step (never go backwards). Used after document saves. */
			preserveActiveStep?: boolean;
			/** Refresh data without unmounting the current step UI. */
			silent?: boolean;
		}) => {
			if (!options?.silent) {
				setIsLoading(true);
			}
			try {
				// Step 1: Get phase two status
				const status = await getOnboardingPhaseTwoStatus();
				if (!status) {
					showToast('error', 'Onboarding status not found');
					if (!options?.silent) setIsLoading(false);
					return;
				}
				setPhaseTwoStatus(status);

				// Step 2: Extract onboarding ID from the status and fetch full onboarding record
				const onboardingId = extractIdFromIRI(status.onboarding);
				let fetchedRecord: OnboardingRecord | null = null;
				if (onboardingId) {
					const record = await getOnboardingById(onboardingId);
					fetchedRecord = record;
					if (record) {
						setOnboardingRecord(record);

						// Fetch workplace details from onboarding record
						const workplaceId = extractIdFromIRI(record.workplace);
						if (workplaceId) {
							try {
								const workplacesMap = JSON.parse(
									localStorage.getItem('workplaces-map') || '{}'
								);
								const matched = workplacesMap[workplaceId.toString()];
								if (matched) setWorkplace(matched);
							} catch (err) {
								console.error('Error reading workplace from localStorage:', err);
							}
						}
					}
				}

				// Step 3: Fetch document templates for the onboarding workplace
				const workplaceIdForDocs = fetchedRecord?.workplace
					? extractIdFromIRI(fetchedRecord.workplace)
					: null;
				const templates = await getOnboardingDocuments(workplaceIdForDocs ?? undefined);
				const requiredTemplates = getRequiredOnboardingDocuments(templates);
				setDocumentTemplates(templates);
				setDocumentTemplatesList(requiredTemplates);

				// Determine active step based on status
				const basicInfoStatus = status.progress?.basic_info?.status;
				const basicInfoCompleted =
					basicInfoStatus === 'completed' || basicInfoStatus === 'approved';
				const requiredStatuses = filterRequiredDocumentStatuses(
					status.progress?.documents || [],
					requiredTemplates,
				);
				const allDocsCompleted =
					requiredTemplates.length === 0 ||
					(requiredStatuses.length > 0 &&
						requiredStatuses.every(
							(doc) => doc.status === 'completed' || doc.status === 'approved',
						));

				let availabilityCompleted =
					!schedulingLicensed || availabilityCompletedLocallyRef.current;
				if (schedulingLicensed && !availabilityCompleted) {
					try {
						const endpoint =
							Number.isFinite(authUserId) && authUserId > 0
								? `/users/${authUserId}`
								: '/users/me';
						const meResponse = await apiService.get(endpoint);
						const meData = apiService.responseHandler(meResponse);
						availabilityCompleted = hasSavedAvailability(
							extractUserAvailabilityFromUser(meData),
						);
					} catch (err) {
						console.error('Error reading user availability during onboarding:', err);
						availabilityCompleted = false;
					}
				}

				if (availabilityCompleted && schedulingLicensed) {
					setAvailabilityCompletedLocally(true);
				}

				const isRecordSubmitted = !!fetchedRecord?.phase2CompletedAt;
				const stepOptions = {
					hasDocuments: requiredTemplates.length > 0,
					schedulingLicensed,
				};
				const availabilityStep = resolveOnboardingStepIndices(stepOptions).availability;
				const resolvedStep = resolveInitialOnboardingStep({
					basicInfoCompleted,
					availabilityCompleted,
					allDocumentsCompleted: allDocsCompleted,
					isSubmitted: isRecordSubmitted,
					hasDocuments: requiredTemplates.length > 0,
					schedulingLicensed,
				});

				// Proceeding from Basic Info always lands on Availability (when licensed),
				// even if availability was saved before, so the employee can review it.
				if (
					options?.proceedFromBasicInfo &&
					basicInfoCompleted &&
					!isRecordSubmitted &&
					availabilityStep != null
				) {
					setActiveStep(availabilityStep);
				} else if (options?.preserveActiveStep) {
					// Document saves refresh status — stay on Documents (or advance forward only).
					setActiveStep((current) => Math.max(current, resolvedStep));
				} else {
					setActiveStep(resolvedStep);
				}
			} catch (error) {
				console.error('Error fetching onboarding data:', error);
				showToast('error', 'Failed to load onboarding information');
			} finally {
				if (!options?.silent) {
					setIsLoading(false);
				}
			}
		},
		[schedulingLicensed, authUserId, showToast],
	);

	// Fetch onboarding data only once on component mount (and if license flag changes)
	useEffect(() => {
		fetchOnboardingData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fetchOnboardingData]);

	const handleFormSubmit = async () => {
		// Basic-info completion may create backend default availability on the user —
		// refresh embedded userAvailability before the Availability step mounts.
		await queryClient.invalidateQueries(['user-availability']);
		await fetchOnboardingData({ proceedFromBasicInfo: true });
		showToast('success', 'Information saved successfully!');
	};

	const handleAvailabilitySubmit = () => {
		setAvailabilityCompletedLocally(true);
		if (stepIndices.availability != null) {
			const nextStep =
				stepIndices.documents != null ? stepIndices.documents : stepIndices.submit;
			setActiveStep(nextStep);
		}
	};

	const handleSubmitPhaseTwo = async () => {
		if (!onboardingRecord) return;
		setIsSubmittingPhaseTwo(true);
		try {
			await submitOnboardingPhaseTwo(onboardingRecord.id);
			showToast('success', 'Onboarding submitted for review!');
			await fetchOnboardingData();
		} catch (error) {
			console.error('Error submitting phase two:', error);
			showToast('error', 'Failed to submit onboarding. Please try again.');
		} finally {
			setIsSubmittingPhaseTwo(false);
		}
	};

	const handleBack = () => {
		if (canGoBack) {
			setActiveStep((prev) => prev - 1);
		}
	};

	const handleNext = () => {
		if (canGoNext && activeStep < steps.length - 1) {
			setActiveStep((prev) => prev + 1);
		}
	};

	if (isLoading) {
		return (
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					minHeight: '100vh',
					backgroundColor: '#f5f5f5',
				}}
			>
				<CircularProgress />
			</Box>
		);
	}

	if (!canViewOwnOnboarding) {
		return (
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					minHeight: '100vh',
					backgroundColor: '#f5f5f5',
				}}
			>
				<Paper sx={{ p: 4, maxWidth: 500 }}>
					<Typography variant="h6" sx={{ mb: 2 }}>
						Access Denied
					</Typography>
					<Typography variant="body1" color="text.secondary">
						You need Onboarding - My view permission to access your onboarding.
					</Typography>
				</Paper>
			</Box>
		);
	}

	if (!phaseTwoStatus) {
		return (
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					minHeight: '100vh',
					backgroundColor: '#f5f5f5',
				}}
			>
				<Paper sx={{ p: 4, maxWidth: 500 }}>
					<Typography variant="h6" sx={{ mb: 2 }}>
						Onboarding Record Not Found
					</Typography>
					<Typography variant="body1" color="text.secondary">
						We couldn&apos;t find your onboarding record. Please contact your manager or HR
						department.
					</Typography>
				</Paper>
			</Box>
		);
	}

	const stepper = (
		<Stepper
			activeStep={activeStep}
			sx={{
				'& .MuiStepLabel-label': { fontSize: '0.875rem' },
				'& .MuiStepIcon-root': { width: 24, height: 24 },
			}}
		>
			{steps.map((label, index) => (
				<Step
					key={label}
					completed={index < activeStep || (isSubmitted && index === activeStep)}
					sx={{ cursor: index < activeStep && !isSubmitted ? 'pointer' : 'default' }}
					onClick={() => {
						if (index < activeStep && !isSubmitted) {
							setActiveStep(index);
						}
					}}
				>
					<StepLabel>{label}</StepLabel>
				</Step>
			))}
		</Stepper>
	);

	return (
		<div className={style.OnboardingProgressContainer}>
			<Container
				maxWidth={isTabletDocumentFocus ? false : 'lg'}
				sx={{ py: isDocumentStep ? 1 : 2, px: isTabletDocumentFocus ? 1.5 : undefined }}
			>
				{isDocumentStep ? (
					<Paper
						sx={{
							px: 1.5,
							py: 0.25,
							mb: 1,
							backgroundColor: '#fff',
						}}
					>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 36 }}>
							<Typography
								variant="subtitle1"
								sx={{
									fontWeight: 600,
									color: 'rgb(68, 99, 136)',
									flex: 1,
									lineHeight: 1.3,
								}}
							>
								Review Documents
							</Typography>
							{!isJourneyExpanded && (
								<Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
									{`Step ${activeStep + 1} of ${steps.length}`}
								</Typography>
							)}
							<IconButton
								size="small"
								onClick={() => setIsJourneyExpanded((expanded) => !expanded)}
								aria-label={isJourneyExpanded ? 'Hide onboarding steps' : 'Show onboarding steps'}
								aria-expanded={isJourneyExpanded}
							>
								<ExpandMoreIcon
									sx={{
										transform: isJourneyExpanded ? 'rotate(180deg)' : 'none',
										transition: 'transform 0.2s',
									}}
								/>
							</IconButton>
						</Box>
						<Collapse in={isJourneyExpanded} unmountOnExit>
							<Box sx={{ pb: 1, pt: 0.5 }}>{stepper}</Box>
						</Collapse>
					</Paper>
				) : (
					<>
						<Paper
							sx={{
								px: 2,
								py: 1.5,
								mb: 2,
								backgroundColor: '#fff',
							}}
						>
							<Typography
								variant="h6"
								sx={{ mb: 1, fontWeight: 600, color: 'rgb(68, 99, 136)' }}
							>
								Welcome to Your Onboarding Journey!
							</Typography>
							{stepper}
						</Paper>

						{workplaceDisplay && (
							<Paper
								sx={{
									px: 2,
									py: 1,
									mb: 2,
									backgroundColor: '#fff',
									border: '1px solid #e0e0e0',
								}}
							>
								<Box
									sx={{
										display: 'flex',
										flexWrap: 'wrap',
										alignItems: 'baseline',
										gap: 0.5,
										rowGap: 0.25,
									}}
								>
									<Typography variant="caption" sx={{ color: 'text.secondary' }}>
										Onboarding Workplace:
									</Typography>
									<Typography
										variant="body2"
										sx={{ fontWeight: 600, color: 'rgb(68, 99, 136)' }}
									>
										{workplaceDisplay.name}
									</Typography>
									{workplaceDisplay.address && (
										<Typography variant="caption" sx={{ color: 'text.secondary' }}>
											{workplaceDisplay.address}
										</Typography>
									)}
								</Box>
							</Paper>
						)}
					</>
				)}

				{/* Content */}
				{activeStep === stepIndices.basicInfo && onboardingRecord && (
					<EmployeeSelfEntryForm
						onboardingRecord={onboardingRecord}
						editable={isFormEditable}
						onSubmit={handleFormSubmit}
					/>
				)}

				{stepIndices.availability != null &&
					activeStep === stepIndices.availability &&
					onboardingWorkplaceId != null && (
						<EmployeeOnboardingAvailabilityForm
							workplaceId={onboardingWorkplaceId}
							editable={isFormEditable}
							onSubmit={handleAvailabilitySubmit}
						/>
					)}

				{activeStep === stepIndices.submit && !hasDocuments && onboardingRecord && !isSubmitted && (
					<Paper sx={{ p: 4, textAlign: 'center' }}>
						<Typography variant="h6" sx={{ mb: 2, color: 'rgb(68, 99, 136)', fontWeight: 600 }}>
							No Documents Required
						</Typography>
						<Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
							There are no documents to fill out for this phase. You can submit your
							onboarding for review.
						</Typography>
						<MuiButton
							variant="contained"
							onClick={handleSubmitPhaseTwo}
							disabled={isSubmittingPhaseTwo || !canSubmitPhaseTwo}
							sx={{
								backgroundColor: 'rgb(68, 99, 136)',
								color: '#fff',
								px: 4,
								py: 1.5,
								'&:hover': {
									backgroundColor: 'rgb(58, 89, 126)',
								},
							'&.Mui-disabled': {
								backgroundColor: 'rgba(68, 99, 136, 0.5)',
								color: 'rgba(255, 255, 255, 0.7)',
							},
						}}
					>
						{isSubmittingPhaseTwo ? <CircularProgress size={24} color="inherit" /> : 'Submit for Review'}
					</MuiButton>
				</Paper>
			)}

			{stepIndices.documents != null &&
				activeStep === stepIndices.documents &&
				hasDocuments &&
				phaseTwoStatus &&
				onboardingRecord && (
				<OnboardingDocumentViewer
					documentStatuses={requiredDocumentStatuses}
					documentTemplates={documentTemplatesList}
					onboardingId={onboardingRecord.id}
					onRefresh={() =>
						fetchOnboardingData({ preserveActiveStep: true, silent: true })
					}
					onAllDocumentsComplete={() => setActiveStep(stepIndices.submit)}
					readOnly={!isFormEditable}
				/>
			)}

			{((stepIndices.complete != null && activeStep === stepIndices.complete) ||
				(activeStep === stepIndices.submit && isSubmitted)) && (
				<Paper sx={{ p: 4 }}>
						<Typography variant="h6" sx={{ mb: 2, color: 'success.main' }}>
							Onboarding Complete!
						</Typography>
						<Typography variant="body1" color="text.secondary">
							You&apos;ve completed your onboarding process. Your manager will review your
							submission.
						</Typography>
					</Paper>
				)}

				{activeStep === stepIndices.submit && !isSubmitted && hasDocuments && onboardingRecord && (
					<Paper sx={{ p: 4, textAlign: 'center' }}>
						<Typography variant="h6" sx={{ mb: 2, color: 'rgb(68, 99, 136)', fontWeight: 600 }}>
							All Documents Completed
						</Typography>
						<Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
							You&apos;ve filled out all required documents. If you need to make changes,
							click on a previous step above to go back and edit.
						</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
							Once submitted, you will not be able to make further changes.
						</Typography>
						<MuiButton
							variant="contained"
							onClick={handleSubmitPhaseTwo}
							disabled={isSubmittingPhaseTwo || !canSubmitPhaseTwo}
							sx={{
								backgroundColor: 'rgb(68, 99, 136)',
								color: '#fff',
								px: 4,
								py: 1.5,
								'&:hover': {
									backgroundColor: 'rgb(58, 89, 126)',
								},
								'&.Mui-disabled': {
									backgroundColor: 'rgba(68, 99, 136, 0.5)',
									color: 'rgba(255, 255, 255, 0.7)',
								},
							}}
						>
							{isSubmittingPhaseTwo ? <CircularProgress size={24} color="inherit" /> : 'Submit for Review'}
						</MuiButton>
					</Paper>
				)}

				{/* Navigation Buttons — hidden on document step; stepper handles navigation there */}
				{!isSubmitted && !isDocumentStep && (
					<Box sx={{ display: 'flex', justifyContent: 'space-between', mt: isTabletDocumentFocus ? 1.5 : 4, mb: 2 }}>
						<MuiButton
							variant="outlined"
							size={isTabletDocumentFocus ? 'large' : 'medium'}
							onClick={handleBack}
							disabled={!canGoBack}
							sx={{
								borderColor: 'rgb(68, 99, 136)',
								color: 'rgb(68, 99, 136)',
								px: isTabletDocumentFocus ? 3 : 4,
								py: isTabletDocumentFocus ? 1.25 : 1.5,
								fontSize: isTabletDocumentFocus ? '1rem' : undefined,
								'&:hover': {
									borderColor: 'rgb(58, 89, 126)',
									backgroundColor: 'rgba(68, 99, 136, 0.04)',
								},
								'&.Mui-disabled': {
									borderColor: 'rgba(68, 99, 136, 0.3)',
									color: 'rgba(68, 99, 136, 0.5)',
								},
							}}
						>
							Back
						</MuiButton>
						<MuiButton
							variant="contained"
							size={isTabletDocumentFocus ? 'large' : 'medium'}
							onClick={handleNext}
							disabled={!canGoNext || activeStep >= steps.length - 1}
							sx={{
								backgroundColor: 'rgb(68, 99, 136)',
								color: '#fff',
								px: isTabletDocumentFocus ? 3 : 4,
								py: isTabletDocumentFocus ? 1.25 : 1.5,
								fontSize: isTabletDocumentFocus ? '1rem' : undefined,
								'&:hover': {
									backgroundColor: 'rgb(58, 89, 126)',
								},
								'&.Mui-disabled': {
									backgroundColor: 'rgba(68, 99, 136, 0.5)',
									color: 'rgba(255, 255, 255, 0.7)',
								},
							}}
						>
							Next
						</MuiButton>
					</Box>
				)}

				{/* Logout Button */}
				{!isTabletDocumentFocus && (
				<Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
					<button
						onClick={(e) => {
							e.preventDefault();
							logout();
						}}
						style={{
							backgroundColor: 'rgb(68, 99, 136)',
							color: '#fff',
							padding: '10px 32px',
							border: 'none',
							borderRadius: '4px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: 500,
							transition: 'background-color 0.2s',
						}}
						onMouseOver={(e) => (e.currentTarget.style.backgroundColor = 'rgb(58, 89, 126)')}
						onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'rgb(68, 99, 136)')}
					>
						Logout
					</button>
				</Box>
				)}
			</Container>
		</div>
	);
}
